import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { ArrowButtonDownBold } from '@ha/icons';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Grid,
  units,
} from '@hbf/dsl';
import { track } from '@hbf/analytics';
import { useIntl } from 'ha/i18n';

const useStyles = makeStyles<void, 'expanded'>()((theme, _params, classes) => ({
  summaryRoot: {
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    minHeight: 'auto',

    '&.Mui-expanded ': {
      minHeight: 'auto',
    },
  },
  icon: {
    fontSize: theme.spacing(1.6),
    color: theme.palette.grey[700],
  },
  summaryContent: {
    margin: theme.spacing(0),

    '&.Mui-expanded ': {
      margin: theme.spacing(0),
    },
  },
  borderTop: {
    borderTop: units.border(1, 'solid', theme.palette.grey[300]),
  },
  root: {
    boxShadow: 'none',
    '&:before': {
      content: 'none',
    },
  },
  expanded: {
    [`&.${classes.expanded}`]: {
      margin: theme.spacing(0),
    },
  },
  detailsRoot: {
    paddingTop: theme.spacing(0),
    paddingLeft: units.rem(1.5),
    paddingRight: units.rem(1.5),
    display: 'block',
  },
  clearButton: {
    textTransform: 'none',
    color: theme.palette.secondary.dark,
  },
}));

interface Props {
  label: string | React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  dataTestLocator: string;
  onClear?: () => void;
  trackText: string;
  topBorder?: boolean;
}

export const FilterAccordion: React.FC<Props> = ({
  label,
  children,
  defaultExpanded = false,
  dataTestLocator,
  onClear,
  trackText,
  topBorder = false,
}) => {
  const { classes, cx } = useStyles();
  const { T } = useIntl();

  // Needs to be stored only on init and not be updated when the prop change.
  const initExpanded = React.useRef(defaultExpanded);

  const onChange = React.useCallback(
    (event, expanded: boolean) => {
      track(trackText, {
        action: expanded ? 'opened' : 'closed',
        filterType: dataTestLocator,
      });
    },
    [trackText, dataTestLocator],
  );

  return (
    <Accordion
      square
      defaultExpanded={initExpanded.current}
      classes={{
        root: cx(classes.root, topBorder && classes.borderTop),
        expanded: classes.expanded,
      }}
      onChange={onChange}
    >
      <AccordionSummary
        expandIcon={<ArrowButtonDownBold className={classes.icon} />}
        classes={{
          root: classes.summaryRoot,
          content: classes.summaryContent,
        }}
        data-test-locator={`${dataTestLocator} Filter Accordion`}
      >
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          {React.isValidElement(label) ? (
            label
          ) : (
            <Typography.ParagraphTitle>{label}</Typography.ParagraphTitle>
          )}
          {onClear && (
            <Button
              variant="text"
              color="secondary"
              className={classes.clearButton}
              onClick={onClear}
            >
              {T('cta.clear')}
            </Button>
          )}
        </Grid>
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.detailsRoot }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};
